import {
  type CostInsightsApi,
  type Alert,
  type Cost,
  type Group,
  type MetricData,
  type Project,
  type Entity,
  type ProductInsightsOptions,
} from '@backstage-community/plugin-cost-insights';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export class CostInsightsClient implements CostInsightsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async fetchFromBackend(path: string): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('cost-insights-aws');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!response.ok) {
      throw new Error(
        `Cost Insights API error: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }

  async getLastCompleteBillingDate(): Promise<string> {
    const data = await this.fetchFromBackend('/last-complete-billing-date');
    return data.date;
  }

  async getUserGroups(_userId: string): Promise<Group[]> {
    return [
      { id: 'platform-engineering', name: 'Platform Engineering' },
    ];
  }

  async getGroupProjects(_group: string): Promise<Project[]> {
    return [{ id: 'cloud-platform-dev' }, { id: 'cloud-platform-prod' }];
  }

  async getGroupDailyCost(
    _group: string,
    intervals: string,
  ): Promise<Cost> {
    const { startDate, endDate } = this.parseIntervals(intervals);
    const data = await this.fetchFromBackend(
      `/daily-costs?startDate=${startDate}&endDate=${endDate}`,
    );
    return this.mapToCost(data);
  }

  async getProjectDailyCost(
    _project: string,
    intervals: string,
  ): Promise<Cost> {
    const { startDate, endDate } = this.parseIntervals(intervals);
    const data = await this.fetchFromBackend(
      `/daily-costs?startDate=${startDate}&endDate=${endDate}`,
    );
    return this.mapToCost(data);
  }

  async getCatalogEntityDailyCost(
    _entityRef: string,
    intervals: string,
  ): Promise<Cost> {
    const { startDate, endDate } = this.parseIntervals(intervals);
    const data = await this.fetchFromBackend(
      `/daily-costs?startDate=${startDate}&endDate=${endDate}`,
    );
    return this.mapToCost(data);
  }

  async getDailyMetricData(
    _metric: string,
    intervals: string,
  ): Promise<MetricData> {
    const { startDate, endDate } = this.parseIntervals(intervals);
    const data = await this.fetchFromBackend(
      `/daily-costs?startDate=${startDate}&endDate=${endDate}`,
    );
    return {
      id: _metric,
      format: 'number',
      aggregation: data.aggregation.map((a: any) => ({
        date: a.id,
        amount: a.amount,
      })),
      change: {
        ratio: 0,
        amount: 0,
      },
    };
  }

  async getProductInsights(
    _options: ProductInsightsOptions,
  ): Promise<Entity> {
    return {
      id: 'aws-services',
      aggregation: [0, 0],
      entities: {},
      change: { ratio: 0, amount: 0 },
    };
  }

  async getAlerts(_group: string): Promise<Alert[]> {
    return [];
  }

  private parseIntervals(intervals: string): {
    startDate: string;
    endDate: string;
  } {
    // ISO 8601 repeating intervals: R{n}/{duration}/{end-date}
    // e.g. R2/P30D/2024-03-15
    const parts = intervals.split('/');
    if (parts.length === 3) {
      const endDate = parts[2];
      const durationStr = parts[1];
      const days = this.parseDurationDays(durationStr);
      const repeats = parseInt(parts[0].replace('R', ''), 10) || 1;
      const totalDays = days * repeats;

      const end = new Date(endDate);
      const start = new Date(end);
      start.setDate(start.getDate() - totalDays);

      return {
        startDate: start.toISOString().split('T')[0],
        endDate,
      };
    }

    // Fallback: last 6 months
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 6);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }

  private parseDurationDays(duration: string): number {
    // Simple parser for P{n}D and P{n}M formats
    const dayMatch = duration.match(/P(\d+)D/);
    if (dayMatch) return parseInt(dayMatch[1], 10);

    const monthMatch = duration.match(/P(\d+)M/);
    if (monthMatch) return parseInt(monthMatch[1], 10) * 30;

    return 30; // default to 30 days
  }

  private mapToCost(data: any): Cost {
    const aggregation = data.aggregation ?? [];

    // Split into two halves for comparison
    const midpoint = Math.floor(aggregation.length / 2);
    const firstHalf = aggregation.slice(0, midpoint);
    const secondHalf = aggregation.slice(midpoint);

    const firstSum = firstHalf.reduce(
      (sum: number, a: any) => sum + a.amount,
      0,
    );
    const secondSum = secondHalf.reduce(
      (sum: number, a: any) => sum + a.amount,
      0,
    );

    const ratio = firstSum > 0 ? (secondSum - firstSum) / firstSum : 0;

    return {
      id: 'aws-cost-explorer',
      aggregation: aggregation.map((a: any) => ({
        date: a.id,
        amount: a.amount,
      })),
      change: {
        ratio,
        amount: secondSum - firstSum,
      },
      trendline: {
        slope: ratio,
        intercept: firstSum > 0 ? firstSum / (firstHalf.length || 1) : 0,
      },
    };
  }
}
